import { FileText } from 'lucide-react'
import { Chip } from '@/components/ds2'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { trackBoxTruckUiInteraction, type BoxTruckDocumentScope } from '../utils/analytics'
import { SnackbarType } from '@/utils/snackbar'
import { DocumentFileName } from './DocumentFileName'
import { DocumentLightbox } from './DocumentLightbox'
import { useDocumentPreview } from 'components/shared/useDocumentPreview'

/** The least a document needs to be a chip: a kind to group by, a name to show, a type to render. */
export interface DocumentChip {
    type: string
    fileName: string
    contentType?: string
}

/**
 * The one documents strip.
 *
 * There were two, and they differed in exactly two things: how a file is ADDRESSED (a driver's documents by
 * their private GCS path, an owner's and a vehicle's by a document id) and which label table names the
 * kinds. Everything else — grouping by type, the chip markup, the lightbox wiring — was copied, which is
 * the arrangement where one of them quietly stops looking like the other.
 *
 * So the address is a callback and the labels are a prop, and the two call sites are thin wrappers that
 * still read as what they are.
 *
 * A failed preview SAYS so. Before, `useDocumentPreview`'s error path only cleared the preview, so a 404, a
 * stale path or a dropped connection was a lightbox that opened and closed again — indistinguishable from
 * the operator having dismissed it themselves, and with nothing to retry.
 */
export const DocumentsCompact = <D extends DocumentChip>({
    docs,
    labels,
    keyOf,
    openFor,
    emptyText,
    scope,
}: {
    docs: D[]
    /** Type → human label. Falls back to the raw type, which is better than nothing and visibly raw. */
    labels?: Record<string, string>
    /** Stable identity for the chip, and what `openFor` will be asked about. */
    keyOf: (doc: D) => string
    openFor: (doc: D) => Promise<string>
    emptyText: string
    /** Whose paperwork this strip shows — the only thing an opened chip reports. Never the file name. */
    scope: BoxTruckDocumentScope
}) => {
    const { showSnackbar } = useSnackbar()
    const { preview, blobUrl, openPreview, closePreview } = useDocumentPreview<D>(() =>
        showSnackbar(SnackbarType.ERROR, 'That file could not be opened. Try again in a moment.')
    )

    if (docs.length === 0) return <p className="text-xs text-foreground-muted">{emptyText}</p>

    // Grouped by TYPE: a record commonly holds several files of one kind — cargo photos, licence front and
    // back — and a flat chip soup makes those indistinguishable.
    const byType = new Map<string, D[]>()
    for (const doc of docs) {
        if (!byType.has(doc.type)) byType.set(doc.type, [])
        byType.get(doc.type)!.push(doc)
    }

    return (
        <>
            <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-3">
                {[...byType.entries()].map(([type, files]) => (
                    <div key={type}>
                        <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-foreground-muted">
                            {labels?.[type] ?? type}
                            {files.length > 1 ? ` (${files.length})` : ''}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {files.map((doc) => (
                                <Chip
                                    key={keyOf(doc)}
                                    variant="secondary"
                                    size="lg"
                                    className="cursor-pointer"
                                    prefix={<FileText size={13} className="shrink-0 text-accent" />}
                                    label={<DocumentFileName fileName={doc.fileName} />}
                                    onClick={() => {
                                        trackBoxTruckUiInteraction('document_previewed', scope)
                                        openPreview(doc, () => openFor(doc))
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {preview && <DocumentLightbox fileName={preview.fileName} contentType={preview.contentType} blobUrl={blobUrl} onClose={closePreview} />}
        </>
    )
}
